import React, { useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import withCssProps from '../../hocs/withCssProps';
import styled, { css, ThemeContext } from 'styled-components';
import { NoScrollBehavior } from '../../behaviours/NoScrollBehavior';
import { Iclose } from '../../atoms/AgroIcon/icons';
var ESCAPE_KEY_CODE = 27;
var AgroModal = withCssProps(function (_ref) {
  var children = _ref.children,
    _ref$closeOnOutsideCl = _ref.closeOnOutsideClick,
    closeOnOutsideClick = _ref$closeOnOutsideCl === void 0 ? true : _ref$closeOnOutsideCl,
    _ref$onDismiss = _ref.onDismiss,
    onDismiss = _ref$onDismiss === void 0 ? function () {} : _ref$onDismiss,
    className = _ref.className,
    _ref$modalAriaLabel = _ref.modalAriaLabel,
    modalAriaLabel = _ref$modalAriaLabel === void 0 ? 'agrofy-modal' : _ref$modalAriaLabel,
    _ref$showCloseBtn = _ref.showCloseBtn,
    showCloseBtn = _ref$showCloseBtn === void 0 ? true : _ref$showCloseBtn,
    _ref$disablePageScrol = _ref.disablePageScroll,
    disablePageScroll = _ref$disablePageScrol === void 0 ? false : _ref$disablePageScrol,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'small' : _ref$variant,
    _ref$backdropMobile = _ref.backdropMobile,
    backdropMobile = _ref$backdropMobile === void 0 ? 'light' : _ref$backdropMobile,
    _ref$backdropDesktop = _ref.backdropDesktop,
    backdropDesktop = _ref$backdropDesktop === void 0 ? 'light' : _ref$backdropDesktop;
  var wrapperRef = useRef(null);
  var theme = useContext(ThemeContext);
  useEffect(function () {
    var handleClickOutside = function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (closeOnOutsideClick) {
          onDismiss();
        }
      }
    };
    var handleKeyUp = function handleKeyUp(event) {
      if (event.keyCode === ESCAPE_KEY_CODE) {
        onDismiss();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keyup', handleKeyUp);
    return function () {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [closeOnOutsideClick, onDismiss]);
  var closeModal = function closeModal(e) {
    e.preventDefault();
    setTimeout(function () {
      onDismiss();
    }, 0);
  };
  var getModal = function getModal() {
    return /*#__PURE__*/React.createElement(ModalWrapper, {
      "aria-label": modalAriaLabel,
      className: className,
      role: "dialog"
    }, /*#__PURE__*/React.createElement(Backdrop, {
      "data-cy": "modal-backdrop",
      backdropMobile: backdropMobile,
      backdropDesktop: backdropDesktop,
      variant: variant
    }, /*#__PURE__*/React.createElement(ModalContainer, {
      variant: variant
    }, /*#__PURE__*/React.createElement(ModalContent, {
      variant: variant,
      ref: wrapperRef,
      tabIndex: -1
    }, showCloseBtn ? /*#__PURE__*/React.createElement(ModalHeader, {
      variant: variant,
      "data-cy": "modal-btnClose"
    }, /*#__PURE__*/React.createElement(CloseButton, {
      onClick: function onClick(e) {
        return closeModal(e);
      },
      variant: variant
    }, /*#__PURE__*/React.createElement(Iclose, {
      height: "24px",
      width: "24px"
    }))) : null, children))));
  };
  return disablePageScroll ? /*#__PURE__*/React.createElement(NoScrollBehavior, null, getModal()) : getModal();
});
AgroModal.displayName = 'AgroModal';
export { AgroModal };
AgroModal.propTypes = {
  children: PropTypes.node,
  closeOnOutsideClick: PropTypes.bool,
  onDismiss: PropTypes.func,
  className: PropTypes.string,
  modalAriaLabel: PropTypes.string,
  showCloseBtn: PropTypes.bool,
  disablePageScroll: PropTypes.bool,
  variant: PropTypes.oneOf(['gallery', 'small', 'fullscreen', 'wsp', 'large']),
  backdropMobile: PropTypes.oneOf(['dark', 'light']),
  backdropDesktop: PropTypes.oneOf(['dark', 'light'])
};
var ModalWrapper = styled.div.withConfig({
  displayName: "AgroModal__ModalWrapper",
  componentId: "sc-1azzooy-0"
})([""]);
var Backdrop = styled.div.withConfig({
  displayName: "AgroModal__Backdrop",
  componentId: "sc-1azzooy-1"
})(["cursor:default;position:fixed;left:0;top:0;bottom:0;right:0;display:flex;background:rgba(11,13,29,0.5);overflow-y:", ";z-index:999999;", " ", " @media (min-width:", "){", " ", "}"], function (_ref2) {
  var variant = _ref2.variant;
  return variant === 'wsp' ? 'none' : 'scroll';
}, function (_ref3) {
  var variant = _ref3.variant,
    backdropMobile = _ref3.backdropMobile;
  return variant === 'gallery' && backdropMobile === 'light' && css(["background:rgba(0,0,0,0.5);"]);
}, function (_ref4) {
  var variant = _ref4.variant,
    backdropMobile = _ref4.backdropMobile;
  return variant === 'gallery' && backdropMobile === 'dark' && css(["background:rgba(0,0,0,1);"]);
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.breakpoint.lg;
}, function (_ref6) {
  var variant = _ref6.variant,
    backdropDesktop = _ref6.backdropDesktop;
  return variant === 'gallery' && backdropDesktop === 'light' && css(["background:rgba(0,0,0,0.5);"]);
}, function (_ref7) {
  var variant = _ref7.variant,
    backdropDesktop = _ref7.backdropDesktop;
  return variant === 'gallery' && backdropDesktop === 'dark' && css(["background:rgba(0,0,0,1);"]);
});
var ModalContainer = styled.div.withConfig({
  displayName: "AgroModal__ModalContainer",
  componentId: "sc-1azzooy-2"
})(["position:absolute;top:", ";left:50%;transform:", ";width:", ";height:", ";", " @media (min-width:", "){top:", ";width:", ";height:auto;max-height:", ";transform:", ";}@media (min-width:", "){top:", ";width:", ";height:auto;transform:", ";max-height:", ";", "}"], function (_ref8) {
  var variant = _ref8.variant;
  return variant === 'small' ? '50%' : '0';
}, function (_ref9) {
  var variant = _ref9.variant;
  return variant === 'small' ? 'translate(-50%, -50%)' : 'translate(-50%, 0)';
}, function (_ref0) {
  var variant = _ref0.variant;
  return variant === 'small' ? '328px' : '100%';
}, function (_ref1) {
  var variant = _ref1.variant;
  return variant === 'fullscreen' || variant === 'large' ? '100%' : 'auto';
}, function (_ref10) {
  var variant = _ref10.variant;
  return variant === 'wsp' && css(["left:18px;top:auto;transform:none;width:auto;bottom:108px;right:24px;"]);
}, function (_ref11) {
  var theme = _ref11.theme;
  return theme.breakpoint.md;
}, function (_ref12) {
  var variant = _ref12.variant;
  return variant === 'small' ? '50%' : variant === 'gallery' ? '0' : variant === 'large' ? '50%' : 'calc(50% - 32vh)';
}, function (_ref13) {
  var variant = _ref13.variant;
  return variant === 'gallery' ? '100%' : '480px';
}, function (_ref14) {
  var variant = _ref14.variant;
  return variant === 'fullscreen' ? '65vh' : variant == 'large' ? '90vh' : 'unset';
}, function (_ref15) {
  var variant = _ref15.variant;
  return variant === 'small' || variant === 'large' ? 'translate(-50%, -50%)' : 'translate(-50%, 0)';
}, function (_ref16) {
  var theme = _ref16.theme;
  return theme.breakpoint.lg;
}, function (_ref17) {
  var variant = _ref17.variant;
  return variant === 'small' ? '50%' : variant === 'gallery' ? '50%' : variant === 'wsp' ? 'auto' : variant === 'large' ? '50%' : 'calc(50% - 32vh)';
}, function (_ref18) {
  var variant = _ref18.variant;
  return variant === 'gallery' ? '956px' : variant == 'large' ? '840px' : '480px';
}, function (_ref19) {
  var variant = _ref19.variant;
  return (variant === 'gallery' || variant === 'large') && 'translate(-50%, -50%)';
}, function (_ref20) {
  var variant = _ref20.variant;
  return variant === 'fullscreen' ? '65vh' : variant == 'large' ? '90vh' : 'unset';
}, function (_ref21) {
  var variant = _ref21.variant;
  return variant === 'wsp' && css(["left:auto;"]);
});
var ModalContent = styled.div.withConfig({
  displayName: "AgroModal__ModalContent",
  componentId: "sc-1azzooy-3"
})(["background:", ";box-shadow:0px 6px 10px rgba(0,0,0,0.15);box-sizing:border-box;border-radius:", ";z-index:999999;padding:", ";display:flex;flex-direction:column;align-items:initial;outline:none;position:relative;height:100%;@media (min-width:", "){padding:", ";border-radius:16px;max-height:", ";}@media (min-width:", "){height:auto;max-height:", ";padding:", ";background:", ";}"], function (_ref22) {
  var theme = _ref22.theme,
    variant = _ref22.variant;
  return variant === 'gallery' ? 'black' : theme.colors.white;
}, function (_ref23) {
  var variant = _ref23.variant;
  return variant === 'small' || variant === 'wsp' ? '16px' : '0';
}, function (_ref24) {
  var variant = _ref24.variant;
  return variant === 'gallery' ? '0' : '24px 32px';
}, function (_ref25) {
  var theme = _ref25.theme;
  return theme.breakpoint.md;
}, function (_ref26) {
  var variant = _ref26.variant;
  return variant === 'gallery' ? '0' : '32px';
}, function (_ref27) {
  var variant = _ref27.variant;
  return variant === 'fullscreen' ? '65vh' : variant === 'large' ? '90vh' : 'unset';
}, function (_ref28) {
  var theme = _ref28.theme;
  return theme.breakpoint.lg;
}, function (_ref29) {
  var variant = _ref29.variant;
  return variant === 'fullscreen' ? '65vh' : variant === 'large' ? '90vh' : 'unset';
}, function (_ref30) {
  var variant = _ref30.variant;
  return variant === 'gallery' ? '0px 76px' : '32px';
}, function (_ref31) {
  var theme = _ref31.theme;
  return theme.colors.white;
});
var CloseButton = styled.button.withConfig({
  displayName: "AgroModal__CloseButton",
  componentId: "sc-1azzooy-4"
})(["all:unset;cursor:pointer;transition:all 0.3s ease;&:hover{opacity:0.8;}button > svg,path{fill:", ";}@media (min-width:", "){button > svg,path{fill:", ";}}"], function (_ref32) {
  var theme = _ref32.theme,
    variant = _ref32.variant;
  return variant === 'gallery' ? theme.colors.white : theme.colors.gray800;
}, function (_ref33) {
  var theme = _ref33.theme;
  return theme.breakpoint.lg;
}, function (_ref34) {
  var theme = _ref34.theme;
  return theme.colors.gray800;
});
var ModalHeader = styled.div.withConfig({
  displayName: "AgroModal__ModalHeader",
  componentId: "sc-1azzooy-5"
})(["position:absolute;top:0;right:0;padding:", ";z-index:10;@media (min-width:", "){padding:24px 24px 0px 0px;}@media (min-width:", "){padding:", ";}"], function (_ref35) {
  var variant = _ref35.variant;
  return variant === 'gallery' ? '16px 16px 0px 0px' : '20px 20px 0px 0px';
}, function (_ref36) {
  var theme = _ref36.theme;
  return theme.breakpoint.md;
}, function (_ref37) {
  var theme = _ref37.theme;
  return theme.breakpoint.lg;
}, function (_ref38) {
  var variant = _ref38.variant;
  return variant === 'gallery' ? '20px 20px 0px 0px' : '24px 24px 0px 0px';
});